import { useParams, Link as RouterLink } from "react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import companyApi from "../api/companyApi";
import favoritesApi from "../api/favoritesApi";
import useAuth from "../hooks/useAuth";
import {
  Container,
  Typography,
  Tabs,
  Tab,
  Box,
  Skeleton,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert
} from "@mui/material";
import { Star, StarBorder, Check as CheckIcon } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getCommodityMeta } from "../components/companies/functions";
import SummaryTab from "../components/companies/SummaryTab";
import FinancialsTab from "../components/companies/FinancialsTab";
import AssetsTab from "../components/companies/AssetsTab";
import OperationsTab from "../components/companies/OperationsTab";
import ForecastsTab from "../components/companies/ForecastsTab";
import {
  trackCompanyClaimCompleted,
  trackCompanyClaimStarted,
  trackCompanyView,
} from "../analytics/ga";

const extractDomainFromUrl = (url) => {
  if (!url) return "";
  try {
    const normalized = url.includes("://") ? url : `https://${url}`;
    const host = new URL(normalized).hostname.toLowerCase();
    return host.startsWith("www.") ? host.slice(4) : host;
  } catch {
    return "";
  }
};

const extractDomainFromEmail = (email) => {
  if (!email || !email.includes("@")) return "";
  const domain = email.split("@", 2)[1].toLowerCase();
  return domain.startsWith("www.") ? domain.slice(4) : domain;
};

const hasOperationsPayloadData = (data) =>
  Boolean(
    (data?.periods && data.periods.length) ||
      (data?.company_summary?.metrics && data.company_summary.metrics.length) ||
      (data?.asset_detail && data.asset_detail.length)
  );

const hasForecastPayloadData = (data) => {
  const forecast = data?.forecast || null;
  return Boolean(
    (forecast?.periods && forecast.periods.length) ||
      (forecast?.company_summary?.metrics && forecast.company_summary.metrics.length) ||
      (forecast?.asset_detail && forecast.asset_detail.length) ||
      (forecast?.resource_detail && forecast.resource_detail.length)
  );
};

export default function CompanyProfilePage() {
  const { user } = useAuth();
  const { slug } = useParams();
  const [company, setCompany] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [tab, setTab] = useState("profile");
  const [claimOpen, setClaimOpen] = useState(false);
  const [relationshipDescription, setRelationshipDescription] = useState("");
  const [proofUrl, setProofUrl] = useState("");
  const [claimFeedback, setClaimFeedback] = useState({
    success: null,
    error: null,
    loading: false,
    autoApproved: false,
  });
  const [companyHasApprovedClaim, setCompanyHasApprovedClaim] = useState(false);
  const [userApprovedClaim, setUserApprovedClaim] = useState(false);
  const [userPendingClaim, setUserPendingClaim] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLoggedIn = Boolean(user && (user.id || user.email || user.username));
  const isAdmin = user?.profile?.role === "admin" || user?.is_staff;
  const canEditCompany = isAdmin || userApprovedClaim;
  const lastTrackedCompanyId = useRef(null);
  const assetsData = company?.public_assets || null;
  const assetsLoading = !company;
  const assetsError = null;
  const operationsAnnualData = company?.public_operations?.annual || null;
  const operationsQuarterlyData = company?.public_operations?.quarterly || null;
  const operationsAnnualLoading = !company;
  const operationsQuarterlyLoading = !company;
  const operationsAnnualError = null;
  const operationsQuarterlyError = null;
  const hasOperationsData =
    hasOperationsPayloadData(operationsAnnualData) || hasOperationsPayloadData(operationsQuarterlyData);
  const hasForecastData =
    hasForecastPayloadData(operationsAnnualData) || hasForecastPayloadData(operationsQuarterlyData);

  const companyId = company?.id;
  const profile = company?.profile || {};
  const userEmail = user?.email || "";
  const companyEmail = userEmail;
  const companyDomain = profile?.website_url ? extractDomainFromUrl(profile.website_url) : "";
  const emailDomain = extractDomainFromEmail(companyEmail);
  const isDomainMatch = Boolean(companyEmail && companyDomain && emailDomain === companyDomain);
  const claimSuccess = Boolean(claimFeedback.success);
  const visibleTabKeys = useMemo(
    () => [
      "profile",
      "financials",
      "assets",
      ...(hasOperationsData ? ["operations"] : []),
      ...(hasForecastData ? ["forecasts"] : []),
    ],
    [hasForecastData, hasOperationsData]
  );

  const loadCompany = useCallback(async () => {
    const data = await companyApi.getCompany(slug);
    setCompany(data);
    if (data?.is_claimed) {
      setCompanyHasApprovedClaim(true);
    }
  }, [slug]);

    useEffect(() => {
      loadCompany();
      if (isLoggedIn) {
        favoritesApi
          .getFavorites()
          .then((data) => setFavorites(Array.isArray(data) ? data : []))
          .catch(() => {});
      }
    }, [loadCompany, isLoggedIn]);

  useEffect(() => {
    if (!visibleTabKeys.includes(tab)) {
      setTab("profile");
    }
  }, [tab, visibleTabKeys]);

  useEffect(() => {
    if (!companyId) return;
    if (lastTrackedCompanyId.current !== companyId) {
      trackCompanyView({
        company_id: companyId,
        company_slug: company?.slug ?? slug,
        ticker: company?.ticker ?? company?.symbol,
      });
      lastTrackedCompanyId.current = companyId;
    }
    let active = true;

    (async () => {
      try {
        if (!isLoggedIn) {
          // rely on company.is_claimed for anon users
          return;
        }
        const data = await companyApi.getClaims({ company: companyId });
        const claims = Array.isArray(data) ? data : data?.results || [];
        const approvedClaims = claims.filter((c) => c.status === "approved");
        const pendingClaims = claims.filter((c) => c.status === "pending");

        const userApproved = approvedClaims.some((c) => String(c.submitted_by) === String(user?.id));
        const userPending = pendingClaims.some((c) => String(c.submitted_by) === String(user?.id));
        const companyApproved = approvedClaims.length > 0 || companyHasApprovedClaim;

        if (!active) return;
        setCompanyHasApprovedClaim(companyApproved);
        setUserApprovedClaim(userApproved);
        setUserPendingClaim(userPending);

      } catch {
        // ignore fetch errors for claim status
      }
    })();

    return () => {
      active = false;
    };
  }, [companyId, company?.slug, slug, company?.ticker, company?.symbol, isLoggedIn, companyHasApprovedClaim, user?.id]);

  const isFavorite = (id) =>
    (company?.id === id && company?.is_favorite) || favorites.some((f) => f.company.id === id);

  const handleToggleFavorite = async (company) => {
    const existing = favorites.find((f) => f.company.id === company.id);
    if (existing) {
      await favoritesApi.removeFavorite(existing.id);
      setFavorites((prev) => prev.filter((f) => f.id !== existing.id));
      setCompany((prev) => (prev?.id === company.id ? { ...prev, is_favorite: false } : prev));
    } else {
      const newFav = await favoritesApi.addFavorite(company.id);
      setFavorites((prev) => [...prev, newFav]);
      setCompany((prev) => (prev?.id === company.id ? { ...prev, is_favorite: true } : prev));
    }
  };

  const handleSubmitClaim = async () => {
    if (!companyId) return;
    if (!companyEmail.trim()) {
      setClaimFeedback({
        loading: false,
        error: "Company email is required.",
        success: null,
        autoApproved: false,
      });
      return;
    }
    if (!isDomainMatch && !relationshipDescription.trim()) {
      setClaimFeedback({
        loading: false,
        error: "Please describe your relationship to the company.",
        success: null,
        autoApproved: false,
      });
      return;
    }
    setClaimFeedback({ success: null, error: null, loading: true, autoApproved: false });
    try {
      const data = await companyApi.submitClaim({
        companyId,
        companyEmail: companyEmail.trim(),
        relationshipDescription: relationshipDescription.trim(),
        proofUrl: proofUrl.trim() || null,
      });
      const autoApproved = Boolean(data?.auto_approved || data?.status === "approved");
      trackCompanyClaimCompleted({
        company_id: companyId,
        company_slug: company?.slug ?? slug,
      });
      setClaimFeedback({
        success: autoApproved
          ? "Claim approved. You can now manage this company profile."
          : "Claim submitted. Our team will review your request and notify you once a decision is made.",
        error: null,
        loading: false,
        autoApproved,
      });
      if (autoApproved) {
        setUserApprovedClaim(true);
        setCompanyHasApprovedClaim(true);
        setUserPendingClaim(false);
      } else {
        setUserPendingClaim(true);
      }
      setRelationshipDescription("");
      setProofUrl("");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.company_email?.[0] ||
        err?.response?.data?.relationship_description?.[0] ||
        err?.response?.data?.non_field_errors?.[0] ||
        "Unable to submit claim. Please try again.";
      setClaimFeedback({ success: null, error: detail, loading: false, autoApproved: false });
    }
  };

  const handleOpenClaimDialog = () => {
    trackCompanyClaimStarted({
      company_id: companyId,
      company_slug: company?.slug ?? slug,
    });
    setClaimOpen(true);
  };

  if (!company)
    return (
      <Container sx={{ mt: 6 }}>
        <Skeleton width="40%" height={60} />
        <Skeleton width="70%" height={30} />
      </Container>
    );

  return (
    <Container
      sx={{
        mt: { xs: 1, md: 1 },
        mb: { xs: 5, md: 8 },
        px: { xs: 1, sm: 3},
      }}
    >
      {/* Back to Directory */}
      <Button
        component={RouterLink}
        to="/companies"
        startIcon={<ArrowBackIcon />}
        variant="text"
        sx={{ mb: 0.5 }}
      >
        Back to Companies
      </Button>

        {/* Frosted Main Container */}
        <Box
          sx={{
            ...theme.mixins.frostedBox,
            p: { xs:1.5, md: 3.5 },
            width: "100%",
            maxWidth: { xs: "100%", md: "100%" },
            mx: "auto",
          }}
        >
          {/* Header */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: isMobile ? "flex-start" : "flex-start",
                  justifyContent: "space-between",
                  gap: isMobile ? 1.25 : 2,
                }}
              >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{
                          lineHeight: 1.2,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {company.name}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: { xs: 0.75, sm: 0 } }}>
                        {[profile.hq_city, profile.hq_country].filter(Boolean).join(", ") || "N/A"}
                      </Typography>
                    </Box>
                    {isMobile && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {companyHasApprovedClaim && (
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: "primary.main",
                            color: "#fff",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 16 }} />
                        </Box>
                      )}
                      <IconButton
                        onClick={() => handleToggleFavorite(company)}
                        sx={{
                          color: isFavorite(company.id) ? "warning.main" : "text.secondary",
                          width: 40,
                          height: 40,
                        }}
                      >
                        {isFavorite(company.id) ? <Star /> : <StarBorder />}
                      </IconButton>
                    </Box>
                    )}
                  </Box>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", width: "100%" }}>
                  {Array.isArray(profile.commodities) &&
                    profile.commodities.map((c) => {
                      const meta = getCommodityMeta(c);
                      return (
                          <Chip
                            key={c}
                            label={meta?.symbol || c}
                            size="small"
                            sx={{
                              backgroundColor: meta?.color || "action.hover",
                              color: "#0f172a",
                              fontWeight: 700,
                              height: 24,
                            }}
                            title={meta?.label || c}
                          />
                        );
                      })}
                  </Box>
                </Box>
                {isMobile && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.75,
                      flexWrap: "wrap",
                      mt: 0.5,
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                      {userApprovedClaim && (
                        <Chip label="You manage this company" color="success" size="small" />
                      )}
                      {userPendingClaim && (
                        <Chip label="Claim pending review" color="warning" size="small" />
                      )}
                    </Box>
                    {(userApprovedClaim || isAdmin) && (
                      <Button
                        component={RouterLink}
                        to={`/companies/${company?.slug}/manage`}
                        variant="outlined"
                        size="small"
                        sx={{ alignSelf: "flex-start" }}
                      >
                        Manage
                      </Button>
                    )}
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "row" : "column",
                  alignItems: isMobile ? "center" : "flex-end",
                  gap: 1,
                  mt: isMobile ? 0 : 0.5,
                  width: isMobile ? "100%" : "auto",
                  justifyContent: isMobile ? "flex-start" : "flex-end",
                  flexWrap: isMobile ? "wrap" : "nowrap",
                }}
              >
                {!isMobile && (
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "nowrap",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {companyHasApprovedClaim && (
                      <Tooltip title="Claimed">
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: "primary.main",
                            color: "#fff",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 16 }} />
                        </Box>
                      </Tooltip>
                    )}
                    {userApprovedClaim && (
                      <Chip label="You manage this company" color="success" size="small" />
                    )}
                    {userPendingClaim && (
                      <Chip label="Claim pending review" color="warning" size="small" />
                    )}
                    {isLoggedIn ? (
                      <IconButton
                        onClick={() => handleToggleFavorite(company)}
                        sx={{
                          color: isFavorite(company.id) ? "warning.main" : "text.secondary",
                          width: 44,
                          height: 44,
                        }}
                      >
                        {isFavorite(company.id) ? <Star /> : <StarBorder />}
                      </IconButton>
                    ) : (
                      <Tooltip title="Log in to favorite companies">
                        <span>
                          <IconButton disabled sx={{ color: "text.secondary", width: 44, height: 44 }}>
                            <StarBorder />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                )}
                {!isMobile && (userApprovedClaim || isAdmin) && (
                  <Button
                    component={RouterLink}
                    to={`/companies/${company?.slug}/manage`}
                    variant="contained"
                    size="small"
                    sx={{ alignSelf: "flex-end", px: 5, mr: 8 }}
                  >
                    Manage
                  </Button>
                )}

                {companyHasApprovedClaim || userApprovedClaim || isAdmin ? null : isLoggedIn ? (
                  userPendingClaim ? (
                    <Tooltip title="Your claim is currently under review.">
                      <span>
                        <Button
                          variant="outlined"
                          disabled
                          fullWidth={isMobile}
                          sx={{ minWidth: { md: 220 }, width: isMobile ? "70%" : "auto" }}
                        >
                          Claim pending review
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleOpenClaimDialog}
                      fullWidth={isMobile}
                      sx={{ minWidth: { md: 240 }, width: isMobile ? "70%" : "auto" }}
                    >
                      Claim this company
                    </Button>
                  )
                ) : (
                  <Tooltip title="Log in to submit a claim">
                    <span>
                      <Button
                        variant="outlined"
                        disabled
                        fullWidth={isMobile}
                        sx={{ minWidth: { md: 220 }, width: isMobile ? "70%" : "auto" }}
                      >
                        Claim this company
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          sx={{
            borderBottom: "1px solid rgba(255,255,255,0.3)",
            mb: 1.5,
            px: { xs: 0, sm: 0 },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minWidth: 0,
              px: { xs: 1, sm: 2 },
              whiteSpace: "nowrap",
            },
          }}
        >
          <Tab label="Profile" value="profile" />
          <Tab label="Financials" value="financials" />
          <Tab label="Assets" value="assets" />
          {hasOperationsData && <Tab label="Operations" value="operations" />}
          {hasForecastData && <Tab label="Forecasts" value="forecasts" />}
        </Tabs>

        {/* --- Profile TAB --- */}
        {tab === "profile" && (
          <Box sx={{ ...theme.mixins.frostedBox, p: { xs: 0.125, md: 2 }, height: "100%" }}>
            <SummaryTab
              mode="profile"
              company={company}
              canEditCompany={canEditCompany}
              onCompanyUpdated={loadCompany}
            />
          </Box>
        )}

        {/* --- FINANCIAL TAB --- */}
        {tab === "financials" && (
          <Box sx={{ ...theme.mixins.frostedBox, p: { xs: 0.125, md: 2 }, height: "100%" }}>
            <FinancialsTab company={company} />
          </Box>
        )}

        {/* --- ASSETS TAB --- */}
        {tab === "assets" && (
          <Box sx={{ ...theme.mixins.frostedBox, p: { xs: 0.125, md: 2 }, height: "100%" }}>
            <AssetsTab data={assetsData} loading={assetsLoading} error={assetsError} />
          </Box>
        )}

        {/* --- OPERATIONS TAB --- */}
        {tab === "operations" && (
          <Box sx={{ ...theme.mixins.frostedBox, p: { xs: 0.125, md: 2 }, height: "100%" }}>
            <OperationsTab
              annualData={operationsAnnualData}
              annualLoading={operationsAnnualLoading}
              annualError={operationsAnnualError}
              quarterlyData={operationsQuarterlyData}
              quarterlyLoading={operationsQuarterlyLoading}
              quarterlyError={operationsQuarterlyError}
            />
          </Box>
        )}

        {/* --- FORECASTS TAB --- */}
        {tab === "forecasts" && (
          <Box sx={{ ...theme.mixins.frostedBox, p: { xs: 0.125, md: 2 }, height: "100%" }}>
            <ForecastsTab
              annualData={operationsAnnualData}
              annualLoading={operationsAnnualLoading}
              annualError={operationsAnnualError}
            />
          </Box>
        )}
      </Box>

      <Dialog
        open={claimOpen}
        onClose={() => setClaimOpen(false)}
        fullWidth
        maxWidth="sm"
        BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,0.25)" } }}
        PaperProps={{
          sx: {
            backgroundColor: "#fff",
            "&:hover": { backgroundColor: "#fff" },
          },
        }}
      >
        <DialogTitle>Claim this company</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          {claimSuccess ? (
            <Alert severity="success">
              {claimFeedback.success}
            </Alert>
          ) : (
            <>
                <Typography variant="body2">
                  Claiming this profile lets you submit updates on behalf of your company. You can learn more about the benefits of claiming your company profile{" "}
                  <Button
                    component={RouterLink}
                    to="/benefits"
                    variant="text"
                    sx={{ p: 0, minWidth: 0, textTransform: "none" }}
                  >
                    here
                  </Button>
                  .
                </Typography>

              {claimFeedback.error && <Alert severity="error">{claimFeedback.error}</Alert>}

              {companyEmail && companyDomain && emailDomain === companyDomain ? (
                <Alert severity="success">
                  Your email domain matches the company website. This claim will be auto-approved.
                </Alert>
              ) : (
                <>
                  <TextField
                    label="Company email (from your account)"
                    type="email"
                    required
                    value={companyEmail}
                    disabled
                    placeholder="you@company.com"
                    fullWidth
                    helperText="We use your verified account email for domain matching."
                  />
                  {companyEmail && companyDomain && (
                    <Typography variant="body2" color="text.secondary">
                      Your email domain doesn't match the company website. Our team will review your request.
                    </Typography>
                  )}
                  {companyEmail && !companyDomain && (
                    <Typography variant="body2" color="text.secondary">
                      We don't have a company website on file, so our team will review your request.
                    </Typography>
                  )}

                  <TextField
                    multiline
                    minRows={3}
                    label="Your role and relationship"
                    required
                    value={relationshipDescription}
                    onChange={(e) => setRelationshipDescription(e.target.value)}
                    placeholder="Example: I am the Investor Relations lead at Example Mining."
                    fullWidth
                  />

                  <TextField
                    label="Proof link (optional)"
                    value={proofUrl}
                    onChange={(e) => setProofUrl(e.target.value)}
                    placeholder="LinkedIn profile, company bio, or press release URL"
                    fullWidth
                  />
                </>
              )}

                <DialogActions sx={{ px: 0, gap: 1, justifyContent: "flex-end", mt: 1 }}>
                  <Button
                    variant="text"
                    component="a"
                    href="mailto:RNAVSales@naturalresourcestocks.net?subject=Claim%20Company"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Contact Sales
                  </Button>
                  <Button onClick={() => setClaimOpen(false)}>Cancel</Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmitClaim}
                    disabled={claimFeedback.loading}
                  >
                    {claimFeedback.loading ? "Submitting..." : "Claim Company"}
                  </Button>
                </DialogActions>
            </>
          )}
        </DialogContent>
        {claimSuccess ? (
          <DialogActions sx={{ px: 3, pb: 3 }}>
            {claimFeedback.autoApproved && (
              <Button
                variant="contained"
                component={RouterLink}
                to={`/companies/${company?.slug ?? slug}/manage`}
                onClick={() => setClaimOpen(false)}
              >
                Go to Management
              </Button>
            )}
            <Button variant={claimFeedback.autoApproved ? "text" : "contained"} onClick={() => setClaimOpen(false)}>
              Close
            </Button>
          </DialogActions>
        ) : null}
      </Dialog>
    </Container>
  );
}
